import { KnownDeliveryContainerDtoType, KnownHubDetailsDtoType } from '@groundfloor-platform/mgmt-service-g-sdk';
import { useMemo, useState } from 'react';
import { useQueryGetHubDetails } from '../../../../lockers/hubs.queries';
import { useForm } from 'react-hook-form';
import { getModalContent } from './switch-space-action.helpers';
import { HubDetails } from '../../../../lockers/models/hub-details.model';
import { naturalSort } from '../../../../../core/util/natural-sort.util';

export interface SwitchSpaceActionProps {
  onSubmit(deliveryContainerID: string, options: { onSuccess(): void; onError(): void }): void;
  hubID: string;
  onAbort(): void;
}

export interface SwitchSpaceForm {
  deliveryContainerID: string;
}

interface SwitchSpaceActionState {}

interface ConfirmationStepState extends SwitchSpaceActionState {
  current: 'CONFIRMATION';
}

interface SendingRequestStepState extends SwitchSpaceActionState {
  current: 'SENDING_REQUEST';
}

interface SuccessStepState extends SwitchSpaceActionState {
  current: 'SUCCESS';
}

interface ErrorStepState extends SwitchSpaceActionState {
  current: 'ERROR';
  error: {
    title: string;
    details: string;
  };
}

type SwitchSpaceActionStates = ConfirmationStepState | SendingRequestStepState | SuccessStepState | ErrorStepState;

const initialState: ConfirmationStepState = {
  current: 'CONFIRMATION'
};

export interface SwitchSpaceActionHandlers {
  onSubmit(form: SwitchSpaceForm): void;
  onClose(): void;
}

export function useSwitchSpaceAction({ hubID, onSubmit, onAbort }: SwitchSpaceActionProps) {
  const [state, setState] = useState<SwitchSpaceActionStates>(initialState);

  const hubData = useQueryGetHubDetails(hubID);

  const form = useForm<SwitchSpaceForm>();

  const deliveryContainerID = form.watch('deliveryContainerID');

  const getSpaces = (hub: HubDetails): DeliveryContainer[] => {
    if (!hub) return [];
    // Return all spaces(from sections) (flatMapped)
    const allSpaces = (hub.sections ?? []).flatMap((section) =>
      section.spaces
        .filter((space) => !space.isKiosk)
        .map((space) => ({
          name: `${section.name} - ${space.name}`,
          id: space.id,
          type: KnownDeliveryContainerDtoType.Space
        }))
    );

    return naturalSort(allSpaces).asc((space) => space.name);
  };

  const selectedDeliveryContainer = useMemo(() => {
    if (!hubData.data) return null;
    if (!deliveryContainerID) return null;

    const selectedDeliveryContainer = getSpaces(hubData.data).find((container) => container.id === deliveryContainerID);
    return selectedDeliveryContainer ?? null;
  }, [deliveryContainerID, hubData.data]);

  const hubContainerSelectOptions = useMemo(() => {
    const options = hubData.data
      ? getSpaces(hubData.data).map((container) => ({
          label: container.name,
          value: container.id
        }))
      : [];
    return naturalSort(options).asc((option) => option.label);
  }, [hubData.data]);

  function onSubmitHandler(form: SwitchSpaceForm) {
    setState(() => ({
      current: 'SENDING_REQUEST'
    }));

    onSubmit(form.deliveryContainerID, {
      onSuccess() {
        setState(() => ({
          current: 'SUCCESS'
        }));
      },
      onError() {
        setState(() => ({
          current: 'ERROR',
          error: {
            title: 'Unable to send switch space request',
            details: 'Please close the modal and try again'
          }
        }));
      }
    });
  }

  const handlers: SwitchSpaceActionHandlers = {
    onSubmit: onSubmitHandler,
    onClose() {
      onAbort();
    }
  };

  const modalContent = useMemo(() => {
    return getModalContent({
      hubName: hubData.data?.name,
      isKioskless: hubData.data?.type === KnownHubDetailsDtoType.KiosklessSpacesHub
    });
  }, [hubData.data]);

  return {
    form,
    state,
    content: modalContent,
    selectedDeliveryContainer,
    hubContainerSelectOptions,
    handlers
  } as const;
}

interface DeliveryContainer {
  id: string;
  name: string;
  type: KnownDeliveryContainerDtoType;
}
